import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

interface Category {
  _id?: string;
  name: string;
  icon: string;
  image: string;
  enabled: boolean;
}

interface CategoriesManagementProps {
  onRefresh: () => void;
}

const CategoriesManagement: React.FC<CategoriesManagementProps> = ({ onRefresh }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<Category>({
    name: '',
    icon: '📦',
    image: '',
    enabled: true
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      alert('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingCategory?._id) {
        await adminAPI.updateCategory(editingCategory._id, formData);
        alert('Category updated successfully!');
      } else {
        await adminAPI.addCategory(formData);
        alert('Category added successfully!');
      }
      setShowAddForm(false);
      setEditingCategory(null);
      setFormData({ name: '', icon: '📦', image: '', enabled: true });
      await loadCategories();
      onRefresh();
    } catch (error: any) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (categoryId: string, currentStatus: boolean) => {
    setLoading(true);
    try {
      await adminAPI.updateCategory(categoryId, { enabled: !currentStatus });
      await loadCategories();
      onRefresh();
    } catch (error) {
      console.error('Error toggling category:', error);
      alert('Failed to update category status');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData(category);
    setShowAddForm(true);
  };

  const handleDelete = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    setLoading(true);
    try {
      await adminAPI.deleteCategory(categoryId);
      alert('Category deleted successfully!');
      await loadCategories();
      onRefresh();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingCategory(null);
    setFormData({ name: '', icon: '📦', image: '', enabled: true });
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Categories Management</h2>
        <button 
          onClick={() => setShowAddForm(true)}
          style={{
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          ➕ Add New Category
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., Groceries, Vegetables, Fruits"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Icon (Emoji) *
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '24px'
                  }}
                  placeholder="📦"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
                  Category Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {formData.image && (
                  <img 
                    src={formData.image} 
                    alt="Preview" 
                    style={{ 
                      width: '100px', 
                      height: '100px', 
                      objectFit: 'cover', 
                      borderRadius: '8px',
                      marginTop: '10px'
                    }} 
                  />
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontWeight: '600' }}>Enable this category</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={cancelForm}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {loading ? 'Saving...' : editingCategory ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories List */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '20px'
      }}>
        {categories.map((category) => (
          <div
            key={category._id}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              background: category.enabled ? 'white' : '#f9fafb',
              position: 'relative',
              opacity: category.enabled ? 1 : 0.6
            }}
          >
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              display: 'flex',
              gap: '5px'
            }}>
              <button
                onClick={() => handleToggleEnabled(category._id!, category.enabled)}
                style={{
                  padding: '5px 10px',
                  background: category.enabled ? '#ef4444' : '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                {category.enabled ? '🔴 Disable' : '🟢 Enable'}
              </button>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
              {category.image ? (
                <img 
                  src={category.image} 
                  alt={category.name}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    margin: '0 auto'
                  }}
                />
              ) : (
                <div style={{ fontSize: '64px' }}>{category.icon}</div>
              )}
            </div>

            <h3 style={{ 
              margin: '0 0 10px 0', 
              fontSize: '18px', 
              textAlign: 'center',
              color: '#1f2937'
            }}>
              {category.name}
            </h3>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '15px'
            }}>
              <span style={{ fontSize: '24px' }}>{category.icon}</span>
              <span style={{
                padding: '4px 12px',
                background: category.enabled ? '#d1fae5' : '#fee2e2',
                color: category.enabled ? '#065f46' : '#991b1b',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {category.enabled ? 'Active' : 'Disabled'}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleEdit(category)}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => handleDelete(category._id!)}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px' }}>
          Loading...
        </div>
      )}

      {!loading && categories.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#6b7280'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📂</div>
          <h3>No categories yet</h3>
          <p>Click "Add New Category" to create your first category</p>
        </div>
      )}
    </div>
  );
};

export default CategoriesManagement;
